/**
 * CrawlProgressModal - Real-time crawl progress display
 * Phase 4: Frontend UI Components
 *
 * Features:
 * - Subscribes to Supabase Realtime for job updates
 * - Displays pages_discovered, pages_crawled, pages_relevant
 * - Progress bar and status indicators
 * - Cancel button (sets job status to 'cancelled')
 * - Handles timeout/error states
 */

import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Loader2, AlertCircle, CheckCircle2, XCircle } from "lucide-react";
import { docsClient } from "@/lib/supabase";
import { CrawlJob } from "@/types/crawl";
import { formatJobStatus } from "@/lib/crawl-utils";
import { RealtimeChannel } from "@supabase/supabase-js";

interface CrawlProgressModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  onComplete: (jobId: string) => void; // Called when crawl completes successfully
  onCancel?: (jobId: string) => void; // Called when user cancels crawl
}

export default function CrawlProgressModal({
  open,
  onOpenChange,
  jobId,
  onComplete,
  onCancel,
}: CrawlProgressModalProps) {
  const [job, setJob] = useState<CrawlJob | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  // Fetch initial job state
  useEffect(() => {
    if (!open || !jobId) return;

    const fetchJob = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data, error } = await docsClient.from("jobs").select("*").eq("id", jobId).single();

        if (error) throw error;

        setJob(data as CrawlJob);
      } catch (err) {
        console.error("Error fetching crawl job:", err);
        setError(err instanceof Error ? err.message : "Failed to fetch crawl job");
      } finally {
        setLoading(false);
      }
    };

    fetchJob();
  }, [open, jobId]);

  // Subscribe to Realtime updates
  useEffect(() => {
    if (!open || !jobId) return;

    let channel: RealtimeChannel;

    const setupRealtimeSubscription = async () => {
      // Subscribe to job updates for this specific job ID
      channel = docsClient
        .channel(`job-${jobId}`)
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "jobs",
            filter: `id=eq.${jobId}`,
          },
          (payload) => {
            console.log("Job update received:", payload);
            setJob(payload.new as CrawlJob);

            // If job completed successfully, notify parent
            if (payload.new.status === "complete") {
              setTimeout(() => {
                onComplete(jobId);
              }, 1000); // Small delay to show completion state
            }
          }
        )
        .subscribe();
    };

    setupRealtimeSubscription();

    // Cleanup subscription on unmount
    return () => {
      if (channel) {
        docsClient.removeChannel(channel);
      }
    };
  }, [open, jobId, onComplete]);

  // Handle cancel button
  const handleCancel = async () => {
    if (!jobId || cancelling) return;

    setCancelling(true);

    try {
      const { error } = await docsClient
        .from("jobs")
        .update({ status: "cancelled" })
        .eq("id", jobId);

      if (error) throw error;

      if (onCancel) {
        onCancel(jobId);
      }

      // Close modal after brief delay
      setTimeout(() => {
        onOpenChange(false);
      }, 500);
    } catch (err) {
      console.error("Error cancelling job:", err);
      setError(err instanceof Error ? err.message : "Failed to cancel job");
    } finally {
      setCancelling(false);
    }
  };

  // Calculate progress percentage
  const getProgressPercentage = (): number => {
    if (!job || !job.job_data) return 0;

    const { pages_discovered, pages_relevant } = job.job_data;

    // Simple progress calculation based on status
    switch (job.status) {
      case "queued":
        return 0;
      case "crawling":
        // Estimate 50% progress when crawling
        return Math.min((pages_discovered / 100) * 50, 50);
      case "filtering":
        // 50-90% during filtering
        return 50 + Math.min((pages_relevant / pages_discovered) * 40, 40);
      case "complete":
        return 100;
      case "failed":
      case "cancelled":
      case "timeout":
        return 100; // Show full bar for terminal states
      default:
        return 0;
    }
  };

  // Get status icon
  const getStatusIcon = () => {
    if (!job) return <Loader2 className="h-5 w-5 animate-spin text-primary" />;

    switch (job.status) {
      case "queued":
      case "crawling":
      case "filtering":
        return <Loader2 className="h-5 w-5 animate-spin text-primary" />;
      case "complete":
        return <CheckCircle2 className="h-5 w-5 text-green-600" />;
      case "failed":
      case "timeout":
        return <XCircle className="h-5 w-5 text-destructive" />;
      case "cancelled":
        return <AlertCircle className="h-5 w-5 text-muted-foreground" />;
      default:
        return <Loader2 className="h-5 w-5 animate-spin text-primary" />;
    }
  };

  const statusInfo = job
    ? formatJobStatus(job.status)
    : { label: "Loading...", variant: "secondary" as const };
  const progressPercentage = getProgressPercentage();
  const jobData = job?.job_data;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]" aria-describedby="crawl-progress-description">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {getStatusIcon()}
            Crawling Documentation
          </DialogTitle>
          <DialogDescription id="crawl-progress-description">
            {job?.status === "complete"
              ? "Crawl complete! Review the results below."
              : "Discovering and analyzing documentation pages..."}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center gap-3 py-8">
            <AlertCircle className="h-12 w-12 text-destructive" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Status Badge */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Status:</span>
              <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
            </div>

            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Progress</span>
                <span className="font-medium">{Math.round(progressPercentage)}%</span>
              </div>
              <Progress value={progressPercentage} className="h-2" aria-label="Crawl progress" />
            </div>

            {/* Stats Grid */}
            {jobData && (
              <div className="grid grid-cols-3 gap-4 rounded-lg border p-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-primary">{jobData.pages_discovered}</p>
                  <p className="text-xs text-muted-foreground">Discovered</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-primary">{jobData.pages_filtered}</p>
                  <p className="text-xs text-muted-foreground">Analyzed</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{jobData.pages_relevant}</p>
                  <p className="text-xs text-muted-foreground">Relevant</p>
                </div>
              </div>
            )}

            {/* Entry URL */}
            {jobData?.entry_url && (
              <div className="rounded-lg bg-muted p-3">
                <p className="text-xs font-medium text-muted-foreground mb-1">Starting from:</p>
                <p className="text-sm truncate" title={jobData.entry_url}>
                  {jobData.entry_url}
                </p>
              </div>
            )}

            {/* Error Message (if failed) */}
            {job?.error && (
              <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3">
                <p className="text-xs font-medium text-destructive mb-1">Error:</p>
                <p className="text-sm text-destructive">{job.error}</p>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {job?.status === "complete" ? (
            <Button onClick={() => onOpenChange(false)} className="w-full">
              Review Results
            </Button>
          ) : job?.status === "failed" || job?.status === "timeout" ? (
            <Button onClick={() => onOpenChange(false)} variant="outline" className="w-full">
              Close
            </Button>
          ) : (
            <Button
              onClick={handleCancel}
              variant="outline"
              disabled={cancelling || job?.status === "cancelled"}
              className="w-full"
            >
              {cancelling ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cancelling...
                </>
              ) : (
                "Cancel Crawl"
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
